import { Heading } from '../../typography/heading/Heading';
import { Text } from '../../typography/text/Text';
import styles from './CategoryBanner.module.scss';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useMedia } from 'react-use';
import { theme } from './_vars';
import { StylesCustomizer } from '../../molecules/stylesCustomizer';
import ImageTeaser from '../imageTeaser/ImageTeaser';
import { useInView } from 'react-intersection-observer';
import { Link } from '../../atoms/link/Link';

const CategoryBanner = ({
  title,
  title_styles,
  subtitle,
  subtitle_styles,
  backgroundColor,
  mobileMedia,
  desktopMedia,
  boxLinkPath,
  minimalHeight,
  minimalHeightSm,
  minimalHeightLg,
  outerBackgroundColor,
  boxPosition,
  innerBackgroundColor,
  boxMaxWidth,
  containerWidth,
  containerIndent,
  containerAlign,
  showOverlay,
  overlayTheme,
}) => {
  const { ref, inView } = useInView();
  const isMobile = useMedia('(max-width: 680px)', false);

  const TitleText = (variables) => {
    return (
      <Heading
        className={clsx(styles.title)}
        as="h2"
        styleAs="h1"
        variables={variables}
      >
        <span
          dangerouslySetInnerHTML={{
            __html: title,
          }}
        />
      </Heading>
    );
  };
  const SubTitleText = (variables) => {
    return (
      <Text
        className={clsx(styles.subtitle)}
        as="span"
        weight={theme === 'Jag' ? 'medium' : 'normal'}
        variables={variables}
      >
        <span
          dangerouslySetInnerHTML={{
            __html: subtitle,
          }}
        />
      </Text>
    );
  };
  const StyledTitle = StylesCustomizer(TitleText);
  const StyledSubTitle = StylesCustomizer(SubTitleText);

  const heightStyles = useMemo(() => {
    const styles = {};

    if (minimalHeight && minimalHeight !== 0) styles['--height'] = `${minimalHeight}px`;
    if (minimalHeightSm && minimalHeightSm !== 0) styles['--heightSm'] = `${minimalHeightSm}px`;
    if (minimalHeightLg && minimalHeightLg !== 0) styles['--heightLg'] = `${minimalHeightLg}px`;

    return styles;
  }, [minimalHeight, minimalHeightSm, minimalHeightLg]);

  const childrenNode = (
    <>
      <ImageTeaser
        preloadImage={inView}
        isMobile={isMobile}
        isMain={true}
        image={desktopMedia}
        imageMobile={mobileMedia}
        isOverlay={showOverlay}
        overlayTheme={overlayTheme}
        hideOnMobile={false}
        heightStyles={heightStyles}
        key="category-banner"
        isLinkCovered={!!boxLinkPath}
        link={boxLinkPath}
        customClasses={{
          root: styles.root,
          wrapper: styles.wrapper,
        }}
      />

      <div
        className={clsx(
          styles.bannerContentInner,
          styles[`boxPosition_${boxPosition}`],
        )}
      >
        <div
          className={clsx(styles.overlayOuter, {
            [styles.hasBackground]: outerBackgroundColor,
          })}
          style={{
            '--outerBackgroundColor':
              outerBackgroundColor && outerBackgroundColor,
            '--maxWidth': boxMaxWidth ? `${boxMaxWidth}%` : '50%',
          }}
        >
          <div
            className={styles.overlay}
            style={{
              '--innerBackgroundColor':
                innerBackgroundColor && innerBackgroundColor,
            }}
          >
            {title && <StyledTitle styles={title_styles}/>}
            {subtitle && <StyledSubTitle styles={subtitle_styles}/>}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div
      ref={ref}
      className={styles.bannerWrapper}
      style={{ margin: containerIndent && containerIndent > 0 ? `0 ${containerIndent}px` : 'auto'}}
    >
      <div
        className={clsx(
          styles.bannerContent,
          styles[`align_${containerAlign}`],
          styles[`bannerWrapperPosition_${boxPosition}`],
        )}
        style={{
          '--backgroundColor':
            backgroundColor !== '' ? backgroundColor : 'transparent',
          '--containerWidth':
            containerWidth && containerWidth <= 100
              ? `${containerWidth}%`
              : `100%`,
        }}
      >
        {boxLinkPath ? (
          <Link link={{reference: boxLinkPath}}>
            {childrenNode}
          </Link>
        ) : (
          childrenNode
        )}
      </div>
    </div>
  );
};

CategoryBanner.propTypes = {
  data: PropTypes.object.isRequired,
};

CategoryBanner.defaultProps = {
  data: {
    overlayTheme: 'dark',
    boxWidth: 'wide',
  },
};

export default CategoryBanner;
